import {
  ButtonLooks,
  FontIcon,
  FontIconPositions,
  FontIcons,
  StandardButton,
  StandardDropdown,
  brandfolderColorPalette,
} from '@brandfolder/react';

import React, { FunctionComponent } from 'react';
import styled from 'styled-components';

import { BrandfolderHexColors } from '@components/library/utils';

import { FILTER_BY_LIST_OPTIONS, SORT_BY_LIST_OPTIONS, filterByMap, sortByMap } from '../constants';
import { FilterValues, SortValues } from '../customPeopleTagTypes';

const PeopleTagsDropdownWrapper = styled(StandardDropdown)`
  .dropdown-button {
    background-color: transparent;
    border-color: ${brandfolderColorPalette.neutrals[300]};
    color: ${brandfolderColorPalette.neutrals[700]};
    justify-content: space-between;
    outline: none;

    &.dropdown-button[aria-expanded='true'],
    &:active,
    &:focus-visible {
      border: 1px solid ${BrandfolderHexColors.PurplePopsicle};
      /* prevents elements from jumping when increasing the border size in active states */
      box-shadow: 0px 0px 0px 1px ${BrandfolderHexColors.PurplePopsicle};
    }

    .bf-button__standard__children {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    span.bf-icon.bff-filters {
      margin-right: 8px;
    }
    span.bf-icon.bff-caret-down {
      font-size: 10px;
    }
  }

  ul.bf-list {
    left: auto !important;
    margin-top: 2px;
    padding: 8px;
    /* changing to absolute positioning so the width of 
    the dropdown menu is relative to the dropdown button */
    position: absolute !important;
    top: auto !important;
    width: 100%;

    .dropdown-list-item {
      color: ${brandfolderColorPalette.neutrals[700]};
      justify-content: flex-start;
      margin: 2px 0;
      min-height: unset;
      outline: none;
      padding: 8px;
      white-space: nowrap;

      &.selected {
        background-color: ${brandfolderColorPalette.neutrals[100]};
      }
    }
  }
`;

type DropdownType = 'sort' | 'filter';

interface DropdownOptions {
  listOptions: { key: string; label: string }[];
  optionsMap: { [key: string]: string };
  defaultSelect: string;
  icon: FontIcons;
}

const getDropdownValues = (type: DropdownType): DropdownOptions => {
  switch (type) {
    case 'sort':
      return {
        listOptions: SORT_BY_LIST_OPTIONS,
        optionsMap: sortByMap,
        defaultSelect: SortValues.UploadDateNewest,
        icon: FontIcons.ArrowUpDown,
      };

    case 'filter':
      return {
        listOptions: FILTER_BY_LIST_OPTIONS,
        optionsMap: filterByMap,
        defaultSelect: FilterValues.Untagged,
        icon: FontIcons.Filters,
      };
  }
};

interface PeopleTagsDropdownProps {
  type: DropdownType;
  selectedValue: string;
  setSelectedValue: (key: string) => void;
}

export const PeopleTagsDropdown: FunctionComponent<PeopleTagsDropdownProps> = ({
  type,
  selectedValue,
  setSelectedValue,
}) => {
  const dropdown = getDropdownValues(type);

  return (
    <PeopleTagsDropdownWrapper
      buttonProps={{ className: 'dropdown-button' }}
      className={`${type}-people-dropdown`}
      closeOnClickAway={false}
      id={`${type}-dropdown`}
      listItems={dropdown.listOptions.map(({ key, label }) => ({
        children: (
          <StandardButton
            className={`dropdown-list-item ${selectedValue === key ? 'selected' : ''}`}
            fullWidth
            look={ButtonLooks.Default}
            onClick={(): void =>
              setSelectedValue(key)
            }
          >
            {label}
          </StandardButton>
        ),
        key,
      }))}
    >
      <FontIcon
        icon={dropdown.icon}
        position={FontIconPositions.Left}
      />
      {dropdown.optionsMap[selectedValue] || dropdown.optionsMap[dropdown.defaultSelect]}
    </PeopleTagsDropdownWrapper>
  );
};
